import { ETLCourse } from './types';

export const STREAMING_ETL_COURSE: ETLCourse = {
  id: 'real-time-streaming-etl',
  title: 'Real-time Streaming ETL Mastery',
  description: 'Master real-time data streaming with Kafka, Spark Streaming, and event-driven architectures. Build enterprise-grade streaming pipelines.',
  level: 'advanced',
  totalDuration: '16-20 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Streaming ETL',
  marketDemand: 'high',
  salaryRange: '$120,000 - $190,000',
  prerequisites: [
    'Strong SQL and Python programming',
    'Experience with batch ETL processes',
    'Understanding of distributed systems',
    'Basic knowledge of Apache Kafka'
  ],
  learningOutcomes: [
    'Design and implement Kafka-based streaming architectures',
    'Build real-time ETL with Apache Spark Streaming',
    'Master event-driven data processing patterns',
    'Implement stream processing with Apache Flink',
    'Handle late-arriving data and out-of-order events',
    'Design fault-tolerant streaming systems'
  ],
  modules: [
    {
      id: 'streaming-etl-1',
      title: 'Apache Kafka and Event Streaming Fundamentals',
      description: 'Master Kafka architecture, topics, partitions, and streaming concepts',
      duration: '6 hours',
      difficulty: 'intermediate',
      order: 1,
      objectives: [
        'Understand Kafka architecture and core concepts',
        'Configure Kafka clusters and topics',
        'Implement producers and consumers',
        'Master partitioning and replication strategies'
      ],
      content: 'Apache Kafka is the backbone of modern streaming architectures. This module covers Kafka fundamentals, cluster setup, topic management, and building robust producer-consumer applications for real-time data streaming.'
    },
    {
      id: 'streaming-etl-2',
      title: 'Spark Streaming and Real-time Processing',
      description: 'Build real-time ETL pipelines with Apache Spark Streaming',
      duration: '5 hours',
      difficulty: 'advanced',
      order: 2,
      objectives: [
        'Develop Spark Streaming applications',
        'Implement windowing and aggregation operations',
        'Handle stateful stream processing',
        'Integrate with various data sources and sinks'
      ],
      content: 'Learn to build sophisticated real-time ETL pipelines using Apache Spark Streaming. Master windowing operations, stateful processing, and integration patterns for continuous data processing.'
    },
    {
      id: 'streaming-etl-3',
      title: 'Apache Flink and Advanced Stream Processing',
      description: 'Master Apache Flink for low-latency stream processing',
      duration: '5 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Build Flink streaming applications',
        'Implement complex event processing (CEP)',
        'Handle exactly-once processing guarantees',
        'Design fault-tolerant streaming architectures'
      ],
      content: 'Master Apache Flink for ultra-low latency stream processing. Learn complex event processing, exactly-once semantics, and building fault-tolerant streaming applications for mission-critical use cases.'
    }
  ]
};

export const DATA_QUALITY_COURSE: ETLCourse = {
  id: 'data-quality-testing-etl',
  title: 'Data Quality & Testing in ETL Pipelines',
  description: 'Master data quality frameworks, automated testing, and monitoring for ETL pipelines. Ensure data reliability and governance.',
  level: 'advanced',
  totalDuration: '12-16 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Data Quality',
  marketDemand: 'high',
  salaryRange: '$105,000 - $175,000',
  prerequisites: [
    'Experience with ETL development',
    'SQL proficiency and data modeling',
    'Understanding of data governance concepts',
    'Basic testing methodologies'
  ],
  learningOutcomes: [
    'Implement comprehensive data quality frameworks',
    'Build automated testing for ETL pipelines',
    'Design data validation and profiling systems',
    'Master data lineage and impact analysis',
    'Implement data monitoring and alerting',
    'Ensure regulatory compliance and governance'
  ],
  modules: [
    {
      id: 'data-quality-1',
      title: 'Data Quality Frameworks and Profiling',
      description: 'Build comprehensive data quality assessment and profiling systems',
      duration: '4 hours',
      difficulty: 'intermediate',
      order: 1,
      objectives: [
        'Design data quality assessment frameworks',
        'Implement automated data profiling',
        'Create data quality scorecards and metrics',
        'Build anomaly detection systems'
      ],
      content: 'Learn to build comprehensive data quality frameworks that automatically assess, profile, and score data quality across your ETL pipelines. Master anomaly detection and quality monitoring.'
    },
    {
      id: 'data-quality-2',
      title: 'Automated Testing and Validation',
      description: 'Implement automated testing strategies for ETL pipelines',
      duration: '4 hours',
      difficulty: 'advanced',
      order: 2,
      objectives: [
        'Build unit and integration tests for ETL',
        'Implement data validation rules',
        'Create automated test suites',
        'Design continuous testing pipelines'
      ],
      content: 'Master automated testing strategies for ETL pipelines. Learn to build comprehensive test suites, implement data validation rules, and create continuous testing frameworks for reliable data processing.'
    },
    {
      id: 'data-quality-3',
      title: 'Data Lineage and Governance',
      description: 'Implement data lineage tracking and governance frameworks',
      duration: '4 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Build data lineage tracking systems',
        'Implement impact analysis capabilities',
        'Design data governance frameworks',
        'Ensure regulatory compliance'
      ],
      content: 'Master data lineage tracking and governance frameworks. Learn to build systems that track data flow, enable impact analysis, and ensure compliance with data regulations and organizational policies.'
    }
  ]
};

export const PERFORMANCE_OPTIMIZATION_COURSE: ETLCourse = {
  id: 'etl-performance-optimization',
  title: 'ETL Performance Optimization & Scaling',
  description: 'Master advanced performance tuning, parallel processing, and scaling strategies for high-volume ETL systems.',
  level: 'advanced',
  totalDuration: '14-18 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Performance',
  marketDemand: 'high',
  salaryRange: '$115,000 - $185,000',
  prerequisites: [
    'Advanced ETL development experience',
    'Database performance tuning knowledge',
    'Understanding of distributed systems',
    'Proficiency in SQL optimization'
  ],
  learningOutcomes: [
    'Optimize ETL pipeline performance and throughput',
    'Implement parallel and distributed processing',
    'Master memory management and resource optimization',
    'Design scalable ETL architectures',
    'Implement advanced caching and partitioning strategies',
    'Monitor and tune production ETL systems'
  ],
  modules: [
    {
      id: 'performance-1',
      title: 'ETL Performance Analysis and Bottleneck Identification',
      description: 'Master performance profiling and bottleneck identification techniques',
      duration: '5 hours',
      difficulty: 'advanced',
      order: 1,
      objectives: [
        'Profile ETL pipeline performance',
        'Identify and analyze bottlenecks',
        'Implement performance monitoring systems',
        'Create performance benchmarking frameworks'
      ],
      content: 'Learn advanced techniques for analyzing ETL performance, identifying bottlenecks, and implementing comprehensive monitoring systems to ensure optimal pipeline performance.'
    },
    {
      id: 'performance-2',
      title: 'Parallel Processing and Distributed ETL',
      description: 'Implement parallel and distributed processing strategies',
      duration: '5 hours',
      difficulty: 'advanced',
      order: 2,
      objectives: [
        'Design parallel ETL architectures',
        'Implement distributed processing patterns',
        'Master workload balancing strategies',
        'Optimize resource utilization'
      ],
      content: 'Master parallel and distributed processing techniques for ETL systems. Learn to design architectures that can scale horizontally and handle massive data volumes efficiently.'
    },
    {
      id: 'performance-3',
      title: 'Advanced Optimization Strategies',
      description: 'Master advanced caching, partitioning, and optimization techniques',
      duration: '4 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Implement advanced caching strategies',
        'Design optimal partitioning schemes',
        'Master memory and storage optimization',
        'Build auto-scaling ETL systems'
      ],
      content: 'Learn advanced optimization strategies including intelligent caching, optimal partitioning, memory management, and building auto-scaling ETL systems that adapt to varying workloads.'
    }
  ]
};

export const ENTERPRISE_ARCHITECTURE_COURSE: ETLCourse = {
  id: 'enterprise-etl-architecture',
  title: 'Enterprise ETL Architecture & Leadership',
  description: 'Master enterprise-scale ETL architecture design, team leadership, and strategic data platform planning for large organizations.',
  level: 'advanced',
  totalDuration: '16-20 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Architecture',
  marketDemand: 'high',
  salaryRange: '$140,000 - $220,000',
  prerequisites: [
    'Extensive ETL development experience',
    'System architecture knowledge',
    'Understanding of enterprise data strategy',
    'Leadership and communication skills'
  ],
  learningOutcomes: [
    'Design enterprise-scale ETL architectures',
    'Lead ETL development teams and projects',
    'Implement data platform governance strategies',
    'Master vendor evaluation and technology selection',
    'Design disaster recovery and business continuity',
    'Develop long-term data platform roadmaps'
  ],
  modules: [
    {
      id: 'enterprise-1',
      title: 'Enterprise ETL Architecture Design',
      description: 'Design scalable, maintainable enterprise ETL architectures',
      duration: '6 hours',
      difficulty: 'advanced',
      order: 1,
      objectives: [
        'Design enterprise-scale ETL architectures',
        'Implement microservices patterns for ETL',
        'Master service orchestration and coordination',
        'Design for high availability and disaster recovery'
      ],
      content: 'Master the design of enterprise-scale ETL architectures that can handle complex organizational requirements, multiple data sources, and massive scale while maintaining reliability and performance.'
    },
    {
      id: 'enterprise-2',
      title: 'ETL Team Leadership and Project Management',
      description: 'Lead ETL development teams and manage complex data projects',
      duration: '5 hours',
      difficulty: 'advanced',
      order: 2,
      objectives: [
        'Lead ETL development teams effectively',
        'Manage complex data integration projects',
        'Implement agile methodologies for ETL',
        'Design team processes and best practices'
      ],
      content: 'Learn to lead ETL development teams, manage complex data projects, and implement effective processes and methodologies for successful enterprise data initiatives.'
    },
    {
      id: 'enterprise-3',
      title: 'Strategic Data Platform Planning',
      description: 'Develop long-term data platform strategies and roadmaps',
      duration: '5 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Develop comprehensive data platform strategies',
        'Create long-term technology roadmaps',
        'Master vendor evaluation and selection',
        'Design platform governance frameworks'
      ],
      content: 'Master strategic planning for enterprise data platforms. Learn to develop comprehensive roadmaps, evaluate technologies, and design governance frameworks for long-term success.'
    }
  ]
};